import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { Typography } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';

import { Debt, getDebtsService } from '../../api/getDebts';
import { initNorskGjeldService } from '../../api/initNorskGjeld';
import { callbackUrl } from '../../api/util';
import Description from '../../components/Description/Description';
import { ErrorMessage } from '../../components/ErrorMessage';
import IconWithText from '../../components/IconWithText/IconWithText';
import Information from '../../components/Information/Information';
import InformationRow from '../../components/Information/InformationRow';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Title from '../../components/Title/Title';
import ValidationErrorMessage from '../../components/ValidationErrorMessage/ValidationErrorMessage';
import { usePagesContext } from '../../context/PagesContext';
import { ConsumerLoanTargetPath } from '../../domain/types';
import { formatNumberByThousands } from '../../helpers/formatNumberByThousands';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import { useConsentIdParams } from '../../hooks/useConsentIdParams';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import { setConsolidations, setInternalLoans } from '../../store/applicationform/applicationFormStore';
import { WizardPageAction } from '../../store/wizard/wizard';
import ConsolidationCheckboxes from './ConsolidationCheckboxes';

const PARAM_CONSENT_STATE = 'consent_state';
const CONSENT_STATE_ERROR = 'error';
const CONSENT_STATE_SUCCESS = 'success';
const APPLICANT_GOVERNMENT_ID = 'applicantGovernmentId';
const APPLICANT_NORSK_GJELD_REFERENCE_ID = 'applicantGjeldsregisteretId';

const ConsolidationPageNO = () => {
    const { countryCode, languageCode } = useSettings();
    const translate = useTranslation();
    const { totalSections, totalAboutYouPages } = usePagesConfig();
    const dispatch = useAppDispatch();
    const { currentPage, pageAction } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();
    useSetBannerType('bannerOne');
    const { apiKey, proxyBaseUrl } = useSettings();
    const [errorOccurred, setErrorOccurred] = useState<boolean>(false);
    const [debts, setDebts] = useState<Debt[]>([]);
    const [showConsolidationsWarning, setShowConsolidationsWarning] = useState<boolean>(false);
    useConsentIdParams('applicantGovernmentId');
    useComponentTracker('ConsolidationPage');

    const { authenticationData } = useAppSelector((state) => state.authentication);
    const selectedConsolidations = useAppSelector((state) => state.form.consolidations);
    const loanAmount = Number.parseInt((useAppSelector(selectFieldStateByName)('amount')?.value as string) || '0');
    const externalConsolidationSum = (selectedConsolidations || []).reduce(
        (sum, debt) => sum + (debt.currentBalance?.amount || 0),
        0,
    );
    const internalLoanSum = debts
        .filter((debt) => debt.nonSelectable)
        .reduce((sum, debt) => sum + (debt.currentBalance?.amount || 0), 0);
    const totalConsolidationSum = internalLoanSum + externalConsolidationSum;
    const totalLoanAmount = Math.max(loanAmount, externalConsolidationSum) + internalLoanSum;
    const cashPayout = Math.max(loanAmount - externalConsolidationSum, 0);
    const applicantGovernmentId = useAppSelector(selectFieldStateByName)(APPLICANT_GOVERNMENT_ID)?.value;
    const norskGjeldReferenceIdFieldState = useAppSelector(selectFieldStateByName)(APPLICANT_NORSK_GJELD_REFERENCE_ID);
    // Raw consolidation intent. False = CASH default.
    // Normalize to an explicit boolean: the field may arrive as a boolean or a 'true'/'false' string.
    const useConsolidationValue = useAppSelector(selectFieldStateByName)('useConsolidation')?.value;
    const useConsolidation = useConsolidationValue === true || useConsolidationValue === 'true';

    useEffect(() => {
        if (!norskGjeldReferenceIdFieldState?.value) {
            return;
        }
        if (!useConsolidation) {
            // CASH: consent collected, debts are not shown. This page has no content,
            // so skip it in whichever direction the user is navigating — forward to the
            // next page, backward to the previous one. Without honoring the direction,
            // pressing Back on the next page would bounce the user forward again.
            defaultPagesAction(pageAction === 'back' ? 'back' : 'next');
            return;
        }
        getDebtsService(
            proxyBaseUrl,
            apiKey,
            norskGjeldReferenceIdFieldState.value + '',
            'MAIN',
            authenticationData,
        )
            .then((response) => {
                setDebts(response.debts);
                const internalLoans = response.debts.filter((debt) => debt.nonSelectable);
                dispatch(setInternalLoans(internalLoans));
                const newDebts = selectedConsolidations.filter((consolidation) =>
                    response.debts.find((debt) => debt.internalId === consolidation.internalId),
                );
                dispatch(setConsolidations(newDebts));
            })
            .catch(() => {
                setDebts([]);
                setErrorOccurred(true);
                dispatch(setConsolidations([]));
                dispatch(setInternalLoans([]));
                dispatch(
                    addOrUpdateFormField({
                        fieldName: APPLICANT_NORSK_GJELD_REFERENCE_ID,
                        value: undefined,
                        hasError: true,
                    }),
                );
            });
    }, [norskGjeldReferenceIdFieldState, useConsolidation, pageAction]);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'collect-norskgjeld') {
            try {
                const successUrl = `${callbackUrl(
                    ConsumerLoanTargetPath.application,
                    languageCode,
                    countryCode,
                    '',
                )}?${PARAM_CONSENT_STATE}=${CONSENT_STATE_SUCCESS}`;
                const failureUrl = `${callbackUrl(
                    ConsumerLoanTargetPath.application,
                    languageCode,
                    countryCode,
                    '',
                )}?${PARAM_CONSENT_STATE}=${CONSENT_STATE_ERROR}`;

                const response = await initNorskGjeldService(
                    proxyBaseUrl,
                    apiKey,
                    applicantGovernmentId as string,
                    successUrl,
                    failureUrl,
                    authenticationData,
                );
                window.location.replace(response.consentUrl);
            } catch (error) {
                setErrorOccurred(true);
                throw error;
            }
            return false;
        } else if (action === 'redirect') {
            window.location.replace('https://www.resursbank.no/lane-penger');
            return defaultPagesAction(action);
        } else if (action === 'next') {
            setShowConsolidationsWarning(false);
            const hasNonSelectableDebts = debts.some((debt) => debt.nonSelectable);
            const hasAnySelection = selectedConsolidations.length > 0 || hasNonSelectableDebts;
            if (!hasAnySelection) {
                setShowConsolidationsWarning(true);
                return false;
            }
            return defaultPagesAction(action);
        }

        return defaultPagesAction(action);
    };

    const handleSelectConsolidation = (event: ChangeEvent<HTMLInputElement>, internalId: string) => {
        const isChecked = event.target.checked;
        const newDebts = debts.filter((debt) => {
            // Never add non-selectable debts to consolidations
            if (debt.nonSelectable) {
                return false;
            }

            const isMatching = debt.internalId === internalId;
            const isAlreadySelected = !!selectedConsolidations.find(
                (consolidation) => consolidation.internalId === debt.internalId,
            );

            if (isChecked) {
                return isMatching || isAlreadySelected;
            } else {
                return !isMatching && isAlreadySelected;
            }
        });

        dispatch(setConsolidations(newDebts));
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            {!norskGjeldReferenceIdFieldState?.value ? (
                <>
                    <Title
                        text={translate(
                            useConsolidation ? 'page.norsk-gjeld.title' : 'page.norsk-gjeld.cash.title',
                        )}
                    />
                    <Description
                        text={translate(
                            useConsolidation
                                ? 'page.norsk-gjeld.description'
                                : 'page.norsk-gjeld.cash.description',
                        )}
                    />
                </>
            ) : (
                <>
                    <Title text={translate('page.norsk-gjeld.consolidations.title')} />
                    {debts && debts.length !== 0 ? (
                        <ShowDetailsModal
                            label={translate('dialog.collect-norsk-gjeld.label')}
                            dialogTitle={translate('dialog.collect-norsk-gjeld.label')}
                            dialogContent={translate('dialog.collect-norsk-gjeld.content')}
                        />
                    ) : null}
                    {debts && debts.length > 0 && (
                        <>
                            <ConsolidationCheckboxes
                                debts={debts}
                                handleChange={handleSelectConsolidation}
                                selectedConsolidations={selectedConsolidations}
                            />
                            {debts.find((debt) => debt.nonSelectable) && (
                                <IconWithText
                                    icon={faCircleExclamation}
                                    text="page.norsk-gjeld.consolidations.internal-loan.text"
                                    iconSize="sm"
                                />
                            )}
                            <Information>
                                <InformationRow
                                    name={translate('field.amount.label')}
                                    value={`${formatNumberByThousands(totalLoanAmount)} ${translate('field.currency.label')}`}
                                />
                                <InformationRow
                                    name={translate('field.consolidation-summary.repayment.label')}
                                    value={`${formatNumberByThousands(totalConsolidationSum)} ${translate('field.currency.label')}`}
                                />
                                <InformationRow
                                    name={translate('field.consolidation-summary.payout.label')}
                                    value={`${formatNumberByThousands(cashPayout)} ${translate('field.currency.label')}`}
                                />
                            </Information>
                        </>
                    )}
                    {debts && debts.length === 0 && (
                        <>
                            <Typography variant={'body1'} marginBottom={2} marginTop={2}>
                                {translate('field.consolidation-amount.description')}
                            </Typography>
                        </>
                    )}
                    {showConsolidationsWarning && (
                        <ValidationErrorMessage>
                            {translate('page.norsk-gjeld.consolidations.required')}
                        </ValidationErrorMessage>
                    )}
                </>
            )}
            {errorOccurred && (
                <ErrorMessage
                    title={translate(`page.error.title`)}
                    description={translate(`page.authentication.submit.log.in.error.description`)}
                />
            )}
            {norskGjeldReferenceIdFieldState?.hasError && (
                <ErrorMessage
                    title={translate('page.norsk-gjeld.consolidations.error.title')}
                    description={translate('page.norsk-gjeld.consolidations.error.description')}
                />
            )}
            <PagesFooter
                actions={
                    !norskGjeldReferenceIdFieldState?.value
                        ? ['collect-norskgjeld']
                        : debts.length === 0
                          ? ['redirect']
                          : ['next']
                }
                onAction={onAction}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default ConsolidationPageNO;
